import React, { useCallback } from "react";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import ArrowUpOnSquareIcon from "@heroicons/react/24/solid/ArrowUpOnSquareIcon";
import { Box, Button, SvgIcon, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import EntityListPage from "../../common/crud/EntityListPage";
import { BusinessTable } from "../../components/Business/Business-table";
import { BusinessSearch } from "../../components/Business/Business-search";
import useBusiness from "../../hooks/store/business";
import { generateCSVBusiness } from "../../api/business";
import { handleDownloadFile } from "../../utils";
import usePermissions from "../../hooks/usePermissions";

const Page = () => {
  const navigate = useNavigate();
  const {
    Data,
    noOfRecords,
    rowsPerPage,
    page,
    loading,
    setPageNumberAction,
    setRowsPerPageAction,
  } = useBusiness();
  const { capabilities } = usePermissions();
  const canManageBusinessDirectory = Boolean(
    capabilities?.canCreateBusinesses
  );
  const [downloadLoading, setDownloadLoading] = React.useState(false);
  const [lastOpenedBusiness, setLastOpenedBusiness] = React.useState(null);

  React.useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem("lastOpenedBusiness");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.id && parsed.name) {
            setLastOpenedBusiness(parsed);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const colorSet = useSelector((state) => state.color);

  const handlePageChange = useCallback(
    (_, value) => {
      setPageNumberAction(value);
    },
    [setPageNumberAction]
  );

  const handleRowsPerPageChange = useCallback(
    (event) => {
      setRowsPerPageAction(event.target.value);
      setPageNumberAction(0);
    },
    [setPageNumberAction, setRowsPerPageAction]
  );

  const handleOpenLastBusiness = useCallback(() => {
    if (!lastOpenedBusiness?.id) {
      return;
    }

    navigate(`/business/edit?id=${lastOpenedBusiness.id}`);
  }, [lastOpenedBusiness, navigate]);

  const handleExport = useCallback(async () => {
    try {
      setDownloadLoading(true);
      const res = await generateCSVBusiness();
      const { data } = res;

      if (data) {
        handleDownloadFile(data, `Business_${new Date().getTime()}.xlsx`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadLoading(false);
    }
  }, []);

  return (
    <EntityListPage
      headerAction={
        canManageBusinessDirectory ? (
          <Button
            component={Link}
            startIcon={
              <SvgIcon fontSize="small">
                <PlusIcon />
              </SvgIcon>
            }
            sx={{
              backgroundColor: colorSet.color,
              ":hover": { backgroundColor: colorSet.color },
              color: "white",
            }}
            to="/business/add"
            variant="contained"
          >
            Add
          </Button>
        ) : null
      }
      loading={loading || downloadLoading}
      search={<BusinessSearch />}
      searchAside={
        lastOpenedBusiness?.id && lastOpenedBusiness?.name ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2">Last opened business:</Typography>
            <Button size="small" variant="text" onClick={handleOpenLastBusiness}>
              {lastOpenedBusiness.name}
            </Button>
          </Box>
        ) : null
      }
      table={
        <BusinessTable
          count={noOfRecords}
          items={Data}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      }
      title="Business"
      toolbar={
        canManageBusinessDirectory ? (
          <Button
            color="inherit"
            startIcon={
              <SvgIcon fontSize="small">
                <ArrowUpOnSquareIcon />
              </SvgIcon>
            }
            onClick={handleExport}
          >
            Export
          </Button>
        ) : null
      }
    />
  );
};

export default Page;
